/**
 * PR-LT3 variant A (A1) — build a bucket's `LineMaterial` with the right dash
 * path (single-pair vs full multi-element texture).
 *
 * # Why this module exists (file-size split)
 *
 * `scene/sceneBulkLoadHelpers.ts` was already ~695 lines (CLAUDE.md "Контроль
 * размера файлов" — do NOT inline-grow). The per-bucket linetype → material
 * decision (resolve the single-pair `DashConfig`, resolve the optional full
 * `DashPattern`, build/encode its texture via the per-pass cache, and construct
 * the `LineMaterial` with the correct uniforms / `USE_DASH_TEXTURE` define) is
 * self-contained and reused per bucket, so it lives here behind a small surface.
 *
 * # The two paths
 *
 *   - **Single-pair fast path** (LT3-B, unchanged): a continuous / solid bucket,
 *     or a faithful 2-element dash/gap (DASHED / HIDDEN). `dashSize` / `gapSize`
 *     from `resolveBucketLinetypeDash`; no texture.
 *   - **Multi-element texture path** (A1): a lossy linetype (CENTER / DASHDOT /
 *     DOT / PHANTOM, `> 2` elements or a dot). `resolveBucketLinetypePattern`
 *     returns the full signed pattern; it is encoded into a 1-D `DataTexture`
 *     (cached by name per pass) and the material flips `USE_DASH_TEXTURE`.
 *
 * Both paths still set `worldUnits:false`, `linewidth:2`, the resolution, and
 * leave `computeLineDistances()` to the caller (the line distances the dash
 * walk consumes are geometry-derived, not material state).
 *
 * # Disposal
 *
 * The pattern textures are owned by the module-scope shared
 * `LinetypePatternTextureCache` (`linetypePatternTexture.ts`), mirroring the
 * hatch pattern-texture cache: tiny (≤16×1 RGBA8) textures are reused across
 * every bucket / bulk-load and cleared on hot reload only. Bucket meshes
 * REFERENCE (do not own) the shared texture, so per-bucket teardown must NOT
 * dispose it. The returned `patternTextureUsed` flag is informational.
 */

import * as THREE from "three";

import { LineMaterial } from "./thickLines";
import {
  resolveBucketLinetypeDash,
  resolveBucketLinetypePattern,
  type LinetypeResolutionMap,
  type LayerLinetypeMap,
} from "./linetypeResolve";
import { getLinetypePatternTextureCache } from "./linetypePatternTexture";

export interface BuildBucketLineMaterialArgs {
  /** Bucket's linetype NAME (MeshKey linetype). */
  linetypeName: string;
  /** Bucket's LAYER NAME (for the ByLayer redirect). */
  layer: string;
  /** Per-pass NON-STANDARD custom LTYPE resolution map. */
  resolution: LinetypeResolutionMap;
  /** Per-pass layer-name → linetype-name map (ByLayer redirect). Optional. */
  layerLinetypes?: LayerLinetypeMap;
  /** Material base color — a CSS hex string or numeric (THREE.ColorRepresentation). */
  colorHex: THREE.ColorRepresentation;
  /** Screen-space line width in pixels (bucket default). */
  linewidth: number;
  /** Resolution uniform (canvas pixels × pixelRatio). */
  resolutionPx: THREE.Vector2;
}

export interface BuildBucketLineMaterialResult {
  material: LineMaterial;
  /** True when the bucket uses the multi-element texture path. */
  patternTextureUsed: boolean;
}

/**
 * Resolve a bucket's linetype into a configured `LineMaterial`, choosing the
 * single-pair `dashSize`/`gapSize` path or the full multi-element texture path.
 */
export function buildBucketLineMaterial(
  args: BuildBucketLineMaterialArgs,
): BuildBucketLineMaterialResult {
  const {
    linetypeName,
    layer,
    resolution,
    layerLinetypes,
    colorHex,
    linewidth,
    resolutionPx,
  } = args;

  // Single-pair config (always resolved — it is the fallback and supplies the
  // `dashed` flag / dash sizes for the non-texture path).
  const dashConfig = resolveBucketLinetypeDash(
    linetypeName,
    resolution,
    layer,
    layerLinetypes,
  );

  // Full multi-element pattern — non-null only for a LOSSY linetype
  // (> 2 elements or a dot). Null → keep the single-pair path.
  const fullPattern = resolveBucketLinetypePattern(
    linetypeName,
    resolution,
    layer,
    layerLinetypes,
  );

  const dashed = dashConfig.dashSize > 0 || fullPattern !== null;

  // PR-LT3 variant A (A2) — FIX (Codex P2). The GLOBAL linetype scale
  // (LTSCALE × MSLTSCALE|PSLTSCALE) is NO LONGER a per-bucket `dashScale`
  // uniform. It is folded into the per-SEGMENT `instanceLtScale` attribute by
  // EACH segment's own model/paper space (see `perSegmentLtScale.ts`), so a
  // MIXED-space bucket (model + paper segments sharing one MeshKey) renders
  // each segment at its OWN space's global scale. `dashScale` is therefore a
  // constant `1`; the per-segment reciprocal (`1 / (global × CELTSCALE)`)
  // carries the whole effective scale into the vertex shader.
  const dashScale = 1;

  const material = new LineMaterial({
    color: colorHex,
    // PR-F2 — minimum 2 px so the sub-pixel thick-line quad always covers a
    // device pixel (see sceneBulkLoadHelpers history).
    linewidth,
    worldUnits: false,
    dashed,
    dashScale,
    dashSize: dashConfig.dashSize,
    gapSize: dashConfig.gapSize,
    resolution: resolutionPx,
    // `alphaToCoverage` stays OFF. It was trialled for the fat-line-continuity
    // (longitudinal beading) fix but a live A/B in the real app proved it a
    // strict NO-OP for continuous lines (byte-identical peak/coverage with it
    // on vs off — see docs/qa/line-continuity-render-notes.md): in the
    // screen-space path `USE_ALPHA_TO_COVERAGE` only reshapes the round-endcap
    // region; the line BODY fragment always carries alpha = opacity = 1.0, so
    // there is no sub-1.0 body alpha for it to dither. The MSAA hardening that
    // DID stick is the explicit `antialias: true` on the Canvas (Scene3DStage).
    alphaToCoverage: false,
    transparent: false,
  });

  // PR-LT3 variant A (A2) — enable the per-instance CELTSCALE term for dashed
  // buckets. The geometry built by `buildSingleBucketMesh` always carries the
  // `instanceLtScale` attribute, so this is safe to flip whenever the material
  // walks a dash pattern (texture or single-pair). Solid buckets skip it (the
  // fragment never reaches the dash walk). Default reciprocal 1 → no per-entity
  // scaling, so a drawing with no group-48 values renders unchanged.
  if (dashed) {
    material.instanceLtScaleEnabled = true;
  }

  let patternTextureUsed = false;
  if (fullPattern !== null) {
    // Build (or reuse) the encoded pattern texture and flip the material to the
    // multi-element walk. The cache keys by pattern CONTENT (not name), so a
    // ByLayer bucket — whose MeshKey name is the sentinel "ByLayer" regardless
    // of the layer's actual linetype — never collides with another layer's
    // pattern, while identical patterns across buckets still reuse one texture.
    const data = getLinetypePatternTextureCache().acquire(fullPattern);
    material.dashPatternTex = data.texture;
    material.dashPatternLength = data.patternLength;
    // Setting the count flips the USE_DASH_TEXTURE define (needsUpdate).
    material.dashElementCount = data.elementCount;
    patternTextureUsed = true;
  }

  return { material, patternTextureUsed };
}
